import axios from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Container,
    Link,
    Typography,
} from '@mui/material';
import {
    GalleryCreateAlbumDialog,
    GalleryDeleteDialog,
    GalleryRemoveFromAlbumDialog,
    GalleryToolbar,
    GalleryUploadDialog,
} from './gallery/GalleryToolbar';
import { GalleryAlbumsSection } from './gallery/GalleryAlbumsSection';
import { GalleryAddExistingPhotosDialog } from './gallery/GalleryAddExistingPhotosDialog';
import { GalleryGrid } from './gallery/GalleryGrid';
import { GalleryLightbox } from './gallery/GalleryLightbox';
import { GallerySliderView } from './gallery/GallerySliderView';
import { useGalleryImages } from '../hooks/useGalleryImages';
import { albumService } from '../api/albums';
import {
    GallerySort,
    GalleryTypeFilter,
    GalleryViewMode,
    Image,
} from '../types/entities/image.entity';

export const Gallery = () => {
    const { t } = useTranslation();
    const { albumId } = useParams<{ albumId?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const initialView = searchParams.get('view') === 'slider' ? 'slider' : 'grid';

    const [typeFilter, setTypeFilter] = useState<GalleryTypeFilter>('all');
    const [sort, setSort] = useState<GallerySort>('newest');
    const [viewMode, setViewMode] = useState<GalleryViewMode>(initialView);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isCreateAlbumOpen, setIsCreateAlbumOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isRemoveFromAlbumOpen, setIsRemoveFromAlbumOpen] = useState(false);
    const [isAddExistingOpen, setIsAddExistingOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState<Image | null>(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const { data: albumData } = useQuery({
        queryKey: ['album', albumId],
        queryFn: () => albumService.getOne(albumId!),
        enabled: !!albumId,
    });

    const { data: albumsListData } = useQuery({
        queryKey: ['albums'],
        queryFn: () => albumService.getAll(1),
    });

    const {
        allImages,
        hasMore,
        isLoading,
        isFetching,
        error,
        loadMore,
        refreshList,
        uploadMutation,
        deleteMutation,
        detachFromAlbumMutation,
        saveDisplayName,
    } = useGalleryImages({
        typeFilter,
        sort,
        albumId: albumId || undefined,
    });

    const createAlbumMutation = useMutation({
        mutationFn: (name: string) => albumService.create(name),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['albums'] });
            setIsCreateAlbumOpen(false);
        },
    });

    useEffect(() => {
        document.title = albumData?.data.album.name ?? t('gallery.title');
    }, [albumData, t]);

    useEffect(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (viewMode === 'slider') {
                next.set('view', 'slider');
            } else {
                next.delete('view');
            }
            return next;
        }, { replace: true });
    }, [viewMode, setSearchParams]);

    const handleViewModeChange = (value: GalleryViewMode) => {
        setViewMode(value);
        setLightboxOpen(false);
    };

    const handleOpenLightbox = (_image: Image, index: number) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const attachImagesMutation = useMutation({
        mutationFn: (imageIds: string[]) => albumService.attachImages(albumId!, imageIds),
        onSuccess: () => {
            refreshList();
            queryClient.invalidateQueries({ queryKey: ['albums'] });
            queryClient.invalidateQueries({ queryKey: ['album', albumId] });
            setIsAddExistingOpen(false);
        },
    });

    const handleRemoveFromAlbumRequest = (image: Image) => {
        setSelectedImage(image);
        setIsRemoveFromAlbumOpen(true);
    };

    const confirmRemoveFromAlbum = () => {
        if (!selectedImage || !albumId) {
            return;
        }

        detachFromAlbumMutation.mutate(selectedImage.id, {
            onSuccess: () => {
                setIsRemoveFromAlbumOpen(false);
                setSelectedImage(null);

                if (lightboxOpen) {
                    if (allImages.length <= 1) {
                        setLightboxOpen(false);
                    } else {
                        setLightboxIndex((prev) => Math.min(prev, allImages.length - 2));
                    }
                }
            },
        });
    };

    const handleDeleteRequest = (image: Image) => {
        setSelectedImage(image);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (!selectedImage) {
            return;
        }

        deleteMutation.mutate(selectedImage.id, {
            onSuccess: () => {
                setIsDeleteModalOpen(false);
                setSelectedImage(null);
                queryClient.invalidateQueries({ queryKey: ['albums'] });

                if (lightboxOpen) {
                    if (allImages.length <= 1) {
                        setLightboxOpen(false);
                    } else {
                        setLightboxIndex((prev) => Math.min(prev, allImages.length - 2));
                    }
                }
            },
        });
    };

    const handleUpload = (files: File[], uploadAlbumId?: string | null) => {
        uploadMutation.mutate(
            { files, albumId: uploadAlbumId ?? albumId ?? null },
            {
                onSuccess: () => {
                    setIsUploadModalOpen(false);
                    queryClient.invalidateQueries({ queryKey: ['albums'] });
                },
            },
        );
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        const serverMessage = axios.isAxiosError(error)
            ? (error.response?.data as { message?: string } | undefined)?.message
            : undefined;
        return (
            <Container sx={{ mt: 4 }}>
                <Alert
                    severity="error"
                    action={(
                        <Button color="inherit" size="small" onClick={refreshList}>
                            {t('common.retry')}
                        </Button>
                    )}
                >
                    {status
                        ? t('common.loadFailed', { status, message: serverMessage || error.message })
                        : `${t('common.error')}: ${error.message}`}
                </Alert>
            </Container>
        );
    }

    const pageTitle = albumId ? albumData?.data.album.name : undefined;

    return (
        <Container sx={{ mt: 4, mb: 6 }}>
            {albumId && (
                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link component={RouterLink} to="/gallery" underline="hover" color="inherit">
                        {t('gallery.title')}
                    </Link>
                    <Typography color="text.primary">{pageTitle ?? '…'}</Typography>
                </Breadcrumbs>
            )}

            {!albumId && (
                <GalleryAlbumsSection
                    activeAlbumId={albumId}
                    onCreateAlbum={() => setIsCreateAlbumOpen(true)}
                />
            )}

            <GalleryToolbar
                title={pageTitle}
                typeFilter={typeFilter}
                sort={sort}
                viewMode={viewMode}
                onTypeFilterChange={setTypeFilter}
                onSortChange={setSort}
                onViewModeChange={handleViewModeChange}
                onUploadClick={() => setIsUploadModalOpen(true)}
                onAddExistingClick={albumId ? () => setIsAddExistingOpen(true) : undefined}
            />

            {!albumId && (
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    {t('gallery.allPhotos')}
                </Typography>
            )}

            {!allImages.length ? (
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
                    <Typography variant="h6" color="text.secondary">
                        {t('gallery.noImages')}
                    </Typography>
                </Box>
            ) : viewMode === 'slider' ? (
                <GallerySliderView
                    images={allImages}
                    hasMore={hasMore}
                    isFetching={isFetching}
                    onDelete={handleDeleteRequest}
                    onLoadMore={loadMore}
                />
            ) : (
                <GalleryGrid
                    images={allImages}
                    onOpen={handleOpenLightbox}
                    onDelete={handleDeleteRequest}
                    onRemoveFromAlbum={albumId ? handleRemoveFromAlbumRequest : undefined}
                />
            )}

            {viewMode === 'grid' && hasMore && (
                <Box display="flex" justifyContent="center" mt={4}>
                    <Button variant="outlined" onClick={loadMore} disabled={isFetching}>
                        {isFetching ? <CircularProgress size={24} /> : t('gallery.loadMore')}
                    </Button>
                </Box>
            )}

            <GalleryUploadDialog
                open={isUploadModalOpen}
                isUploading={uploadMutation.isPending}
                defaultAlbumId={albumId ?? null}
                onClose={() => setIsUploadModalOpen(false)}
                onUpload={handleUpload}
            />

            <GalleryCreateAlbumDialog
                open={isCreateAlbumOpen}
                isSaving={createAlbumMutation.isPending}
                onClose={() => setIsCreateAlbumOpen(false)}
                onCreate={(name) => createAlbumMutation.mutate(name)}
            />

            <GalleryDeleteDialog
                open={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setSelectedImage(null);
                }}
                onConfirm={confirmDelete}
            />

            {albumId && (
                <>
                    <GalleryRemoveFromAlbumDialog
                        open={isRemoveFromAlbumOpen}
                        onClose={() => {
                            setIsRemoveFromAlbumOpen(false);
                            setSelectedImage(null);
                        }}
                        onConfirm={confirmRemoveFromAlbum}
                    />

                    <GalleryAddExistingPhotosDialog
                        open={isAddExistingOpen}
                        albumId={albumId}
                        isSaving={attachImagesMutation.isPending}
                        onClose={() => setIsAddExistingOpen(false)}
                        onAttach={(imageIds) => attachImagesMutation.mutate(imageIds)}
                    />
                </>
            )}

            <GalleryLightbox
                open={lightboxOpen}
                images={allImages}
                currentIndex={lightboxIndex}
                onClose={() => setLightboxOpen(false)}
                onChangeIndex={setLightboxIndex}
                onDelete={handleDeleteRequest}
                onRemoveFromAlbum={albumId ? handleRemoveFromAlbumRequest : undefined}
                onDisplayNameSave={saveDisplayName}
                albums={albumsListData?.data.albums ?? []}
                currentAlbumId={albumId}
            />
        </Container>
    );
};
